import React, {Component, ReactNode} from 'react'
import {Nullable, Optional} from '../../app/TypeHelpers'
import {SelectedUser} from '../../__NoAutomatedTests/components/common/resident/NatResidentSelectedUser'
import {
    PaymentInformationForReservation,
    PaymentItem,
    PaymentItemForReservation,
    ReservationOption,
} from './ReserveFacilityModel'
import moment from 'moment'
import {convertToNullableMoment} from './helpers/helpers'
import {formatNumberWithCommas} from '../../formatters/formatNumberWithCommas'
import FacilityForReservation from '../facilities/FacilityForReservation'
import ReservationDetailItem from './components/ReservationDetailItem'
import {BlockSpacer} from '../../styledComponents/ResponsiveConvenience'
import {PAYMENT_TYPE} from '../../CodeMaster'
import {comparePaymentItem} from './helpers/compareHelper'
import {convertToRoomNumberForResident} from '../../__NoAutomatedTests/common/util/Natutil'

interface Props {
    facilityInfo: FacilityForReservation
    reserveInfo: ReserveFacilityConfirmData
}

export interface ReserveFacilityConfirmData {
    startDate: string
    startTime: Nullable<string>
    endDate: Nullable<string>
    endTime: Nullable<string>
    stayCount: Nullable<number>
    reservationTitle: string
    sharedUseRoomNumberList: SelectedUser[]
    options: ReservationOption[]

    paymentInformation: PaymentInformationForReservation
}

class ResidentReserveFacilityConfirmComponent extends Component<Props> {

    constructor(props: Readonly<Props>) {
        super(props)
    }

    render(): ReactNode {
        const {overnightFacilitySetting} = this.props.facilityInfo
        const {startDate, reservationTitle, sharedUseRoomNumberList, options} = this.props.reserveInfo
        const {items, totalCharge} = this.props.reserveInfo.paymentInformation

        let startDateTimeString: string
        let endDateTimeString: string
        let stayCountString: Nullable<string> = null
        if (this.props.facilityInfo.stayAvailability) {
            const {stayCount} = this.props.reserveInfo
            if (overnightFacilitySetting === null) {
                throw Error('OvernightFacilitySetting is null')
            }
            if (stayCount === null) {
                throw Error('StayCount is null')
            }
            stayCountString = `${stayCount}泊`
            const startDateTimeMoment: Nullable<moment.Moment> =
                convertToNullableMoment(startDate, overnightFacilitySetting.checkinTime)
            const endDateMoment: Nullable<moment.Moment> =
                convertToNullableMoment(startDate, overnightFacilitySetting.checkoutTime)
            if (startDateTimeMoment === null) {
                throw Error('StartDate is null')
            }
            if (endDateMoment === null) {
                throw Error('EndDate is null')
            }
            const endDateTimeMoment = endDateMoment.clone().add(stayCount, 'day')
            startDateTimeString = startDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
            endDateTimeString = endDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
        } else {
            const {startTime, endDate, endTime} = this.props.reserveInfo
            const startDateTimeMoment: Nullable<moment.Moment> =
                convertToNullableMoment(startDate, startTime)
            const endDateTimeMoment: Nullable<moment.Moment> =
                convertToNullableMoment(endDate, endTime)
            if (startDateTimeMoment === null) {
                throw Error('StartDate is null')
            }
            if (endDateTimeMoment === null) {
                throw Error('EndDate is null')
            }
            startDateTimeString = startDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
            endDateTimeString = endDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
        }

        if (totalCharge === null) {
            throw Error('totalCharge is null')
        }

        const totalChargeString: string = formatNumberWithCommas(totalCharge) + '円'
        const optionList: PaymentItem[] =
            [...items]
                .filter((item) => item.id !== null)
                .map((paymentItemForReservation: PaymentItemForReservation) => {
                    const {paymentType, ...rest} = paymentItemForReservation
                    const reservationOption: Optional<ReservationOption> =
                        options.find(option => option.optionId === paymentItemForReservation.id)
                    if (!reservationOption) {
                        throw new Error('option not found')
                    }
                    return {
                        ...rest,
                        paymentType: PAYMENT_TYPE.codeToEnumConstants(paymentType as string),
                        displayOrder: reservationOption.sortOrder,
                    } as PaymentItem
                })
                .sort((a, b) => comparePaymentItem(a, b))

        return <>
            <ReservationDetailItem
                type={'texts'}
                title={'施設名'}
                texts={[this.props.facilityInfo.facilityName]}
            />
            <BlockSpacer height={'5px'}/>
            <ReservationDetailItem
                type={'texts'}
                title={'利用日時'}
                texts={[
                    `${startDateTimeString}から`,
                    `${endDateTimeString}まで`,
                ]}
            />
            {
                stayCountString !== null &&
                <>
                    <BlockSpacer height={'5px'}/>
                    <ReservationDetailItem
                        type={'texts'} title={'宿泊数'}
                        texts={[stayCountString]}
                    />
                </>
            }
            {
                (reservationTitle && reservationTitle !== '') &&
                <>
                    <BlockSpacer height={'5px'}/>
                    <ReservationDetailItem
                        type={'texts'} title={'予約タイトル'}
                        texts={[reservationTitle]}
                    />
                </>
            }
            {
                (sharedUseRoomNumberList.length > 0) &&
                <>
                    <BlockSpacer height={'5px'}/>
                    <ReservationDetailItem
                        type={'texts'} title={'同時入室番号'}
                        texts={sharedUseRoomNumberList.sort((a, b) => {
                            const aName = `${a.ridgeCd}${a.roomNumber}`
                            const bName = `${b.ridgeCd}${b.roomNumber}`
                            if (aName === bName) {
                                return 0
                            }
                            return (aName > bName) ? 1 : -1
                        }).map((user) =>
                            `${user.ridgeName}  ${convertToRoomNumberForResident(user.roomNumber)}`,
                        )}
                    />
                </>
            }
            {
                (optionList.length > 0) &&
                <>
                    <BlockSpacer height={'5px'}/>
                    <ReservationDetailItem
                        type={'texts'} title={'オプション'}
                        texts={optionList.map((item) => `${item.article}：${item.quantityWithUnit}`)}
                    />
                </>
            }
            <BlockSpacer height={'5px'}/>
            <ReservationDetailItem
                type={'texts'}
                title={'お支払い合計金額'}
                texts={[totalChargeString]}
            />
        </>
    }
}

export default ResidentReserveFacilityConfirmComponent
